import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  Plus,
  Search,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Pencil,
  Check,
  X,
  Rocket,
  Upload,
  Settings2,
  Mail,
  Phone,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import ComposeTab from "./ComposeTab";
import WavesTab from "./WavesTab";
import ResultsTab from "./ResultsTab";
import TrackingTab from "./TrackingTab";
import NewCampaignWizard from "./NewCampaignWizard";
import ImportCsvModal from "./ImportCsvModal";
import EditSchoolModal from "./EditSchoolModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { key: "directory", label: "Directory" },
  { key: "compose", label: "Compose" },
  { key: "waves", label: "Waves" },
  { key: "results", label: "Results" },
  { key: "tracking", label: "Tracking" },
];

const buildHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
  "Content-Type": "application/json",
});

const formatDeadline = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

// ─────────────────────────────────────────────────────────────────────
// Campaign Switcher (left rail)
// ─────────────────────────────────────────────────────────────────────

function CampaignCard({ campaign, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3.5 py-3 rounded-lg border transition-all"
      style={{
        background: active ? "#0f172a" : "#fff",
        color: active ? "#fff" : "#0f172a",
        borderColor: active ? "#0f172a" : "#e5e7eb",
        boxShadow: active ? "0 2px 6px rgba(15,23,42,0.18)" : "none",
      }}
      data-testid={`campaign-card-${campaign.key || campaign.id}`}
    >
      <div className="text-[13px] font-bold leading-snug">{campaign.name}</div>
      <div className="mt-1 text-[11px]" style={{ color: active ? "rgba(255,255,255,0.7)" : "#64748b" }}>
        {campaign.state} · {campaign.school_count} school{campaign.school_count === 1 ? "" : "s"} · {formatDeadline(campaign.deadline_iso)} deadline
      </div>
    </button>
  );
}

function CampaignSwitcher({ campaigns, activeId, onSelect, onNew }) {
  return (
    <aside
      className="shrink-0 w-full lg:w-[260px] rounded-2xl border border-[#e5e7eb] bg-white p-3 self-start"
      data-testid="campaign-switcher"
    >
      <div className="px-1 pb-2 text-[10px] font-bold tracking-[0.14em] uppercase text-[#94a3b8]">
        Campaigns
      </div>
      <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-1 px-1">
        {campaigns.map((c) => (
          <div key={c.id} className="shrink-0 w-[240px] lg:w-auto">
            <CampaignCard
              campaign={c}
              active={c.id === activeId}
              onClick={() => onSelect(c.id)}
            />
          </div>
        ))}
        <div className="shrink-0 w-[240px] lg:w-auto">
          <button
            type="button"
            onClick={onNew}
            className="w-full inline-flex items-center justify-center gap-1.5 h-[58px] lg:h-9 lg:mt-1 rounded-lg border border-dashed text-[12px] font-semibold text-[#475569] hover:bg-[#f8fafc] transition-colors"
            style={{ borderColor: "#cbd5e1" }}
            data-testid="campaign-switcher-new-btn"
          >
            <Plus className="h-3.5 w-3.5" /> New Campaign
          </button>
        </div>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Directory tab
// ─────────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  queued: { bg: "#f1f5f9", fg: "#475569" },
  booked: { bg: "#dbeafe", fg: "#1d4ed8" },
  opened: { bg: "#fef3c7", fg: "#a16207" },
  replied: { bg: "#dcfce7", fg: "#15803d" },
};

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.queued;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold tracking-[0.04em] uppercase"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`status-pill-${status}`}
    >
      {status}
    </span>
  );
}

function VerifiedBadge({ verified }) {
  if (verified) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
        style={{ background: "#dcfce7", color: "#15803d" }}
        title="Verified"
        data-testid="verified-badge-yes"
      >
        <CheckCircle2 className="h-3 w-3" /> Verified
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
      style={{ background: "#fef3c7", color: "#a16207" }}
      title="Needs review"
      data-testid="verified-badge-no"
    >
      <AlertTriangle className="h-3 w-3" /> Review
    </span>
  );
}

function InlineEditable({ value, onSave, testid, small = false, placeholder = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { onSave(draft); setEditing(false); }
            if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
          }}
          className="px-2 py-1 text-[12px] border border-[#cbd5e1] rounded-md min-w-[180px]"
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          onClick={() => { onSave(draft); setEditing(false); }}
          className="p-1 rounded hover:bg-[#dcfce7]"
          data-testid={`${testid}-save`}
        >
          <Check className="h-3.5 w-3.5 text-[#15803d]" />
        </button>
        <button
          type="button"
          onClick={() => { setDraft(value || ""); setEditing(false); }}
          className="p-1 rounded hover:bg-[#fee2e2]"
        >
          <X className="h-3.5 w-3.5 text-[#b91c1c]" />
        </button>
      </span>
    );
  }
  const displayValue = value || (placeholder ? <em className="text-[#94a3b8]">{placeholder}</em> : <em className="text-[#94a3b8]">—</em>);
  return (
    <span
      onClick={() => setEditing(true)}
      className={`group cursor-pointer inline-flex items-center gap-1.5 hover:bg-[#f8fafc] px-1.5 py-0.5 rounded ${small ? "text-[12px]" : "text-[13px]"}`}
      data-testid={testid}
    >
      <span className={small ? "text-[#475569]" : "text-[#0f172a] font-semibold"}>{displayValue}</span>
      <Pencil className="h-3 w-3 text-[#94a3b8] opacity-0 group-hover:opacity-100 transition-opacity" />
    </span>
  );
}

function LeaderRow({ leader, onUpdate, onGenerateNote, busyNote }) {
  return (
    <div
      className="flex items-start gap-3 py-2 border-b last:border-b-0"
      style={{ borderColor: "#f1f5f9" }}
      data-testid={`leader-row-${leader.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <InlineEditable
            value={leader.leader_name}
            onSave={(v) => onUpdate(leader.id, { leader_name: v })}
            testid={`leader-name-${leader.id}`}
          />
          <VerifiedBadge verified={leader.verified} />
        </div>
        <div className="mt-0.5">
          <InlineEditable
            value={leader.leader_role}
            onSave={(v) => onUpdate(leader.id, { leader_role: v })}
            testid={`leader-role-${leader.id}`}
            small
          />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1 text-[11.5px] text-[#64748b]">
            <Mail className="h-3 w-3 text-[#94a3b8]" />
            <InlineEditable
              value={leader.email || ""}
              onSave={(v) => onUpdate(leader.id, { email: v })}
              testid={`leader-email-${leader.id}`}
              small
              placeholder="add email"
            />
          </span>
          <span className="inline-flex items-center gap-1 text-[11.5px] text-[#64748b]">
            <Phone className="h-3 w-3 text-[#94a3b8]" />
            <InlineEditable
              value={leader.phone || ""}
              onSave={(v) => onUpdate(leader.id, { phone: v })}
              testid={`leader-phone-${leader.id}`}
              small
              placeholder="add phone"
            />
          </span>
        </div>
        {leader.ai_note && (
          <div
            className="mt-1.5 inline-flex items-start gap-1.5 text-[11.5px] rounded-md px-2 py-1 max-w-[640px]"
            style={{ background: "rgba(59,79,228,0.06)", color: "#1e293b" }}
            data-testid={`leader-ai-note-${leader.id}`}
          >
            <Sparkles className="h-3 w-3 mt-0.5 shrink-0 text-[#3B4FE4]" />
            <span>{leader.ai_note}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onGenerateNote(leader.id)}
        disabled={busyNote}
        className="shrink-0 inline-flex items-center gap-1 px-2 h-7 rounded-md text-[11px] font-semibold border transition-colors hover:bg-[#f1f5f9] disabled:opacity-50"
        style={{ borderColor: "#cbd5e1", color: "#3B4FE4" }}
        title="Generate AI research note"
        data-testid={`leader-ai-btn-${leader.id}`}
      >
        {busyNote ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        AI Note
      </button>
    </div>
  );
}

function SchoolRow({ school, onUpdateLeader, onUpdateSchool, onGenerateNote, onEdit, busyLeaderId }) {
  return (
    <div
      className="rounded-xl border bg-white p-4"
      style={{ borderColor: "#e5e7eb" }}
      data-testid={`school-row-${school.school_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div data-testid={`school-name-${school.school_id}`}>
            <InlineEditable
              value={school.school_name}
              onSave={(v) => onUpdateSchool(school.school_id, { school_name: v })}
              testid={`school-name-edit-${school.school_id}`}
            />
          </div>
          <div className="mt-0.5 text-[11.5px] text-[#64748b]">
            {school.city}, {school.state} · {school.county} County · {" "}
            <a href={`https://${school.website}`} target="_blank" rel="noreferrer" className="hover:underline">{school.website}</a>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onEdit(school)}
            className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-[#e5e7eb] text-[11px] font-semibold text-[#475569] hover:bg-[#f8fafc]"
            title="Open full editor"
            data-testid={`school-edit-btn-${school.school_id}`}
          >
            <Settings2 className="h-3 w-3" /> Edit
          </button>
          <StatusPill status={school.status} />
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-[#f1f5f9]">
        {(school.leaders || []).length === 0 ? (
          <div className="text-[12px] text-[#94a3b8] py-2">No leaders match the current filter.</div>
        ) : (
          school.leaders.map((L) => (
            <LeaderRow
              key={L.id}
              leader={L}
              onUpdate={onUpdateLeader}
              onGenerateNote={onGenerateNote}
              busyNote={busyLeaderId === L.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid, all = "All" }) {
  return (
    <label className="inline-flex flex-col gap-1 text-[11px] font-semibold text-[#475569]">
      {label}
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a] min-w-[140px]"
        data-testid={testid}
      >
        <option value="">{all}</option>
        {options.map((o) => (
          <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
        ))}
      </select>
    </label>
  );
}

function DirectoryTab({ session, campaign }) {
  const [state, setState] = useState("");
  const [county, setCounty] = useState("");
  const [status, setStatus] = useState("");
  const [verified, setVerified] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyLeaderId, setBusyLeaderId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // { school, leaders }

  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = useCallback(async () => {
    if (!campaign?.id) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (state) params.set("state", state);
    if (county) params.set("county", county);
    if (status) params.set("status", status);
    if (verified) params.set("verified", verified);
    if (q) params.set("q", q);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaign.id}/schools?${params}`, { headers });
      if (r.ok) {
        setData(await r.json());
      } else {
        toast.error("Failed to load directory");
      }
    } finally {
      setLoading(false);
    }
  }, [campaign?.id, state, county, status, verified, q, headers]);

  useEffect(() => { load(); }, [load]);

  // Reset filters when campaign changes
  useEffect(() => {
    setState("");
    setCounty("");
    setStatus("");
    setVerified("");
    setQ("");
  }, [campaign?.id]);

  const handleUpdateLeader = useCallback(async (leaderId, patch) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/charter-leaders/${leaderId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error("update failed");
      toast.success("Saved");
      load();
    } catch {
      toast.error("Could not save");
    }
  }, [headers, load]);

  const handleUpdateSchool = useCallback(async (schoolId, patch) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/charter-schools/${schoolId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error("update failed");
      toast.success("Saved");
      load();
    } catch {
      toast.error("Could not save");
    }
  }, [headers, load]);

  const handleGenerateNote = useCallback(async (leaderId) => {
    setBusyLeaderId(leaderId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/charter-leaders/${leaderId}/ai-note`, {
        method: "POST",
        headers,
      });
      if (!r.ok) throw new Error("ai note failed");
      toast.success("AI note generated");
      load();
    } catch {
      toast.error("AI note generation failed");
    } finally {
      setBusyLeaderId(null);
    }
  }, [headers, load]);

  const handleGenerateAll = useCallback(async () => {
    if (!campaign?.id) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/campaigns/${campaign.id}/generate-all-notes?only_missing=true&limit=40`, {
        method: "POST",
        headers,
      });
      const j = await r.json();
      if (r.ok) {
        toast.success(`Generated ${j.generated || 0} note(s) · ${j.failed || 0} failed`);
        load();
      } else {
        toast.error("Bulk generation failed");
      }
    } catch {
      toast.error("Bulk generation failed");
    } finally {
      setBulkBusy(false);
    }
  }, [campaign?.id, headers, load]);

  const schools = data?.schools || [];
  const filters = data?.filters || { states: [], counties: [], statuses: ["queued", "booked", "opened", "replied"] };

  return (
    <div data-testid="directory-tab">
      {/* Deadline bar + toolbar */}
      <div
        className="rounded-xl px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5"
        style={{ background: "#fffbeb", border: "1px solid #fde68a" }}
        data-testid="directory-deadline-bar"
      >
        <div className="text-[12.5px] font-semibold text-[#92400e]">
          {campaign?.state} · {data?.total_in_campaign ?? campaign?.school_count} schools · {formatDeadline(campaign?.deadline_iso)} deadline
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold bg-white border border-[#fde68a] hover:bg-[#fef3c7] text-[#92400e]"
            data-testid="directory-import-csv-btn"
          >
            <Upload className="h-3.5 w-3.5" /> Import / Export CSV
          </button>
          <button
            type="button"
            onClick={handleGenerateAll}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold bg-white border border-[#fde68a] hover:bg-[#fef3c7] text-[#92400e] disabled:opacity-50"
            data-testid="generate-all-notes-btn"
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Generate AI Notes
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Select label="State" value={state} onChange={(v) => { setState(v); setCounty(""); }} options={filters.states} testid="filter-state" all="All states" />
        <Select label="County" value={county} onChange={setCounty} options={filters.counties} testid="filter-county" all="All counties" />
        <Select label="Status" value={status} onChange={setStatus} options={filters.statuses} testid="filter-status" all="Any status" />
        <Select label="Verified" value={verified} onChange={setVerified} options={[{ value: "verified", label: "Verified" }, { value: "review", label: "Needs review" }]} testid="filter-verified" all="Any" />
        <label className="inline-flex flex-col gap-1 text-[11px] font-semibold text-[#475569] flex-1 min-w-[200px]">
          Search
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94a3b8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="School name..."
              className="w-full h-9 pl-8 pr-3 rounded-md border border-[#e5e7eb] bg-white text-[13px]"
              data-testid="filter-search"
            />
          </div>
        </label>
      </div>

      {/* Schools list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
        </div>
      ) : schools.length === 0 ? (
        <div className="text-center py-16 text-[#94a3b8]" data-testid="directory-empty">
          No schools match the current filters.
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="directory-schools-list">
          {schools.map((s) => (
            <SchoolRow
              key={s.school_id}
              school={s}
              onUpdateLeader={handleUpdateLeader}
              onUpdateSchool={handleUpdateSchool}
              onGenerateNote={handleGenerateNote}
              onEdit={(school) => setEditing({ school, leaders: school.leaders || [] })}
              busyLeaderId={busyLeaderId}
            />
          ))}
        </div>
      )}

      <ImportCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
        session={session}
        campaignId={campaign?.id}
        campaignName={campaign?.name}
      />

      <EditSchoolModal
        open={!!editing}
        school={editing?.school}
        leaders={editing?.leaders}
        session={session}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main Panel
// ─────────────────────────────────────────────────────────────────────

export default function CampaignsPanel({ session }) {
  const [campaigns, setCampaigns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeTab, setActiveTab] = useState("directory");
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const headers = useMemo(() => buildHeaders(session), [session]);

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      let r = await fetch(`${BACKEND_URL}/api/admin/campaigns`, { headers });
      let list = r.ok ? await r.json() : [];
      if (!list.length) {
        await fetch(`${BACKEND_URL}/api/admin/campaigns/seed`, { method: "POST", headers });
        r = await fetch(`${BACKEND_URL}/api/admin/campaigns`, { headers });
        list = r.ok ? await r.json() : [];
      }
      setCampaigns(list);
      if (list.length && !activeId) setActiveId(list[0].id);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [headers, activeId]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.id === activeId),
    [campaigns, activeId],
  );

  const handleNewCampaign = () => setWizardOpen(true);

  const handleWizardCreated = (created) => {
    loadCampaigns();
    if (created?.id) setActiveId(created.id);
  };

  const handleLaunch = () => {
    toast("Launch flow is MOCKED", {
      description: `Use the Compose tab → Send Wave to update statuses for ${activeCampaign?.school_count || 0} schools in ${activeCampaign?.name}.`,
    });
  };

  if (loadingCampaigns) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="campaigns-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  return (
    <div data-testid="campaigns-panel">
      {/* Title bar */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-[28px] font-bold tracking-[-0.02em] text-[#0f172a] flex items-center gap-2.5" data-testid="campaigns-title">
            <Megaphone className="h-6 w-6 text-[#C79A3B]" />
            Campaigns
          </h1>
          <p className="mt-1 text-[13px] text-[#64748b]">
            Manage outreach campaigns, school directories, and AI-assisted research notes.
          </p>
        </div>
      </div>

      {/* Body: left rail + main area — stacks on mobile/tablet, side-by-side on lg+ */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 lg:items-start">
        <CampaignSwitcher
          campaigns={campaigns}
          activeId={activeId}
          onSelect={setActiveId}
          onNew={handleNewCampaign}
        />

        <div className="flex-1 min-w-0">
          {/* Tab bar + Launch button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div
              className="inline-flex items-center rounded-lg border border-[#e5e7eb] bg-white p-1 overflow-x-auto max-w-full"
              data-testid="campaign-tab-bar"
            >
              {TABS.map((t) => {
                const active = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className="px-4 h-8 rounded-md text-[12.5px] font-semibold transition-colors"
                    style={{
                      background: active ? "#0f172a" : "transparent",
                      color: active ? "#fff" : "#475569",
                    }}
                    data-testid={`tab-${t.key}`}
                    aria-pressed={active}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={handleLaunch}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-semibold text-white shadow-[0_4px_12px_rgba(199,154,59,0.35)] hover:opacity-95 transition-opacity"
              style={{ background: "#C79A3B" }}
              data-testid="launch-campaign-btn"
            >
              <Rocket className="h-4 w-4" /> Launch Campaign
            </button>
          </div>

          {/* Tab content */}
          {activeCampaign && activeTab === "directory" && (
            <DirectoryTab session={session} campaign={activeCampaign} />
          )}
          {activeCampaign && activeTab === "compose" && (
            <ComposeTab session={session} campaign={activeCampaign} />
          )}
          {activeCampaign && activeTab === "waves" && (
            <WavesTab session={session} campaign={activeCampaign} onChanged={loadCampaigns} />
          )}
          {activeCampaign && activeTab === "results" && (
            <ResultsTab session={session} campaign={activeCampaign} />
          )}
          {activeCampaign && activeTab === "tracking" && (
            <TrackingTab session={session} campaign={activeCampaign} />
          )}
        </div>
      </div>

      <NewCampaignWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={handleWizardCreated}
        session={session}
      />
    </div>
  );
}
